import { useEffect, useRef, useState } from "react";
import { QrCode } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const DEFAULTS = { enabled: true, x: 0.68, y: 0.68, size: 0.26, label: true, cover: true, product_key: null };

export default function QrOverlayEditor({ resource, products = [], onClose, onSaved }) {
  const [ov, setOv] = useState({ ...DEFAULTS, ...(resource.qr_overlay || {}), enabled: true });
  const boxRef = useRef(null);
  const imgWrapRef = useRef(null);
  const drag = useRef(null); // { mode: 'move'|'resize', ... }

  // keep box height square (in fraction of height) based on rendered aspect
  const sizeFracH = () => {
    const r = imgWrapRef.current?.getBoundingClientRect();
    if (!r || !r.height) return ov.size;
    return (ov.size * r.width) / r.height;
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    const r = imgWrapRef.current.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    if (drag.current.mode === "move") {
      const nx = clamp(fx - drag.current.offX, 0, 1 - ov.size);
      const sfh = (ov.size * r.width) / r.height;
      const ny = clamp(fy - drag.current.offY, 0, 1 - sfh);
      setOv((o) => ({ ...o, x: nx, y: ny }));
    } else if (drag.current.mode === "resize") {
      // size grows as pointer moves right of box's left edge
      const newSize = clamp((e.clientX - r.left) / r.width - ov.x, 0.08, 0.6);
      setOv((o) => {
        const sfh = (newSize * r.width) / r.height;
        return { ...o, size: newSize, x: clamp(o.x, 0, 1 - newSize), y: clamp(o.y, 0, 1 - sfh) };
      });
    }
  };
  const onPointerUp = () => { drag.current = null; };

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  });

  const startMove = (e) => {
    e.preventDefault();
    const r = imgWrapRef.current.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    drag.current = { mode: "move", offX: fx - ov.x, offY: fy - ov.y };
  };
  const startResize = (e) => { e.preventDefault(); e.stopPropagation(); drag.current = { mode: "resize" }; };

  const save = async (enabled) => {
    try {
      await api.put(`/admin/resources/${resource.id}/qr-overlay`, { ...ov, enabled });
      toast.success(enabled ? "Personal-QR overlay saved" : "Overlay disabled");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const boxStyle = {
    left: `${ov.x * 100}%`,
    top: `${ov.y * 100}%`,
    width: `${ov.size * 100}%`,
    aspectRatio: "1 / 1",
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={onClose} data-testid="qr-overlay-modal">
      <div className="relative w-full max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white" data-testid="qr-overlay-close"><span className="text-xl leading-none">×</span></button>
        <div className="flex items-center gap-2"><QrCode className="h-5 w-5 text-[#F97316]" /><h3 className="font-display text-xl font-bold text-white">Personal-QR overlay</h3></div>
        <p className="mt-1 text-xs text-zinc-500">Drag the box onto the spot where each ambassador's personal QR should appear — place it right over any generic QR already in your poster to replace it.</p>

        <div className="mt-4 grid gap-5 md:grid-cols-[1fr_240px]">
          {/* canvas */}
          <div className="flex items-start justify-center">
            <div ref={imgWrapRef} className="relative w-full max-w-[460px] select-none overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
              <img src={fileUrl(resource.file_path)} alt={resource.name} className="block h-auto w-full" draggable={false} />
              <div
                ref={boxRef}
                onPointerDown={startMove}
                className="absolute cursor-move rounded-md border-2 border-[#F97316] bg-white/85 shadow-lg"
                style={boxStyle}
                data-testid="qr-overlay-box"
              >
                <div className="flex h-full w-full flex-col items-center justify-center text-zinc-900">
                  <QrCode className="h-1/2 w-1/2" />
                  {ov.label && <span className="mt-0.5 text-[7px] font-bold leading-none">SCAN TO REGISTER</span>}
                </div>
                <div
                  onPointerDown={startResize}
                  className="absolute -bottom-1.5 -right-1.5 h-4 w-4 cursor-nwse-resize rounded-sm border-2 border-white bg-[#F97316]"
                  data-testid="qr-overlay-resize"
                />
              </div>
            </div>
          </div>

          {/* controls */}
          <div className="space-y-4 text-sm">
            <label className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <span className="text-zinc-300">"SCAN TO REGISTER" label</span>
              <input type="checkbox" checked={ov.label} onChange={(e) => setOv({ ...ov, label: e.target.checked })} data-testid="qr-overlay-label" className="h-4 w-4 accent-[#F97316]" />
            </label>
            <label className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <span className="text-zinc-300">White card (hides baked-in QR)</span>
              <input type="checkbox" checked={ov.cover} onChange={(e) => setOv({ ...ov, cover: e.target.checked })} data-testid="qr-overlay-cover" className="h-4 w-4 accent-[#F97316]" />
            </label>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">QR points to</label>
              <Select value={ov.product_key || "__choose__"} onValueChange={(v) => setOv({ ...ov, product_key: v === "__choose__" ? null : v })}>
                <SelectTrigger className="border-zinc-800 bg-zinc-900" data-testid="qr-overlay-product"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__choose__">Let ambassador choose</SelectItem>
                  {products.map((p) => <SelectItem key={p.product_key} value={p.product_key}>{p.product_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-zinc-600">
              Position: {Math.round(ov.x * 100)}% / {Math.round(ov.y * 100)}% · Size: {Math.round(ov.size * 100)}% of width
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button onClick={() => save(true)} className="rounded-md bg-[#F97316] px-4 py-2 text-sm font-bold text-white hover:bg-[#ea6a0e]" data-testid="qr-overlay-save">Enable & save</button>
              {resource.qr_overlay?.enabled && (
                <button onClick={() => save(false)} className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900" data-testid="qr-overlay-disable">Disable overlay</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
